import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Users, Compass, Zap, Moon, Sprout, MessageSquare, Sparkles, ShieldCheck, X, Check } from "lucide-react";
import { useLang } from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LeadForm } from "@/components/LeadForm";
import { Reveal, FadeStagger, FadeItem } from "@/components/Motion";
import WorldHero from "@/components/WorldHero";

const PILLAR_ICONS = [Users, Compass, Zap, Moon, Sprout, MessageSquare];

const DEMO_SHOTS = ["/platform-sostenibilidad.webp", "/platform-colaboracion.webp", "/platform-eficiencia.webp", "/platform-liderazgo.webp", "/platform-comunicacion.webp", "/platform-asistente-ia.webp"];

const SUITE_GROUPS = [
  {
    name: "Google Workspace",
    logo: "/logos/googleworkspace.svg",
    apps: [
      { name: "Gmail", logo: "/logos/gmail.svg" },
      { name: "Calendar", logo: "/logos/gcalendar.svg" },
      { name: "Chat", logo: "/logos/gchat.svg" },
      { name: "Meet", logo: "/logos/gmeet.svg" },
    ],
  },
  {
    name: "Microsoft 365",
    logo: "/logos/microsoft365.svg",
    showName: true,
    apps: [
      { name: "Outlook", logo: "/logos/outlook.svg" },
      { name: "Teams", logo: "/logos/teams.svg" },
      { name: "Calendar", logo: "/logos/mscalendar.png" },
    ],
  },
];

const CONNECTOR_LOGOS = [
  { src: "/logos/zoom.svg", name: "Zoom", h: "h-9" },
  { src: "/logos/slack.svg", name: "Slack", h: "h-9" },
  { src: "/logos/workday.svg", name: "Workday", h: "h-8" },
  { src: "/logos/sap.svg", name: "SAP SuccessFactors", h: "h-9" },
];

const AIRobot = () => (
  <div
    data-testid="people-ai-robot"
    aria-hidden="true"
    className="group relative h-20 w-16 cursor-pointer select-none transition-transform duration-500 hover:rotate-6 hover:scale-105"
  >
    <div className="absolute -top-2.5 left-1/2 h-3 w-[2px] -translate-x-1/2 rounded-full bg-[#353535]/30" />
    <div className="robot-antenna absolute -top-4 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-[#ff764a]" />
    <div className="robot-bob absolute inset-x-0 top-2 bottom-0 rounded-2xl border border-black/10 bg-white p-2 shadow-[0_14px_30px_rgba(53,53,53,0.12)]">
      <div className="flex h-[62%] w-full items-center justify-center gap-2.5 rounded-xl bg-[#0d9488]/10">
        <span className="robot-eye h-2.5 w-2.5 rounded-full bg-[#0d9488]" />
        <span className="robot-eye h-2.5 w-2.5 rounded-full bg-[#0d9488]" style={{ animationDelay: "0.09s" }} />
      </div>
      <div className="mx-auto mt-1.5 h-2 w-6 rounded-b-full bg-[#0d9488]/70 transition-all duration-500 group-hover:w-8 group-hover:bg-[#ff764a]" />
    </div>
  </div>
);

const PeopleWorld = () => {
  const { t } = useLang();
  const p = t.people;
  const [demoTab, setDemoTab] = useState(0);
  const [chatAnswered, setChatAnswered] = useState(false);
  const [leadService, setLeadService] = useState("");

  const scrollToLead = (service) => (e) => {
    e.preventDefault();
    setLeadService(service);
    document.getElementById("people-lead")?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const timer = setTimeout(() => setChatAnswered(true), 2800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div data-testid="people-world" className="bg-[#FAFAFD] text-[#353535]">
      <Navbar accent="#0d9488" />

      <WorldHero
        testid="people-hero"
        accent="#0d9488"
        kicker={p.kicker}
        l1={p.heroL1}
        l2={p.heroL2}
        sub={p.heroSub}
        cta1={{ label: p.ctaDemo, to: "#people-lead", onClick: scrollToLead("demo") }}
        cta2={{ label: p.ctaDiscover, to: "#people-pillars" }}
        note={p.demoNote}
      >
        <div data-testid="people-hero-visual" className="relative w-full max-w-md">
          <div className="absolute -top-11 -right-1 z-10 sm:-right-3">
            <AIRobot />
          </div>
          <div className="rounded-2xl border border-black/10 bg-white p-5 sm:p-6 shadow-[0_24px_60px_rgba(53,53,53,0.07)]">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1 }}
              className="ml-auto w-fit max-w-[88%] rounded-2xl rounded-br-md bg-[#353535] px-4 py-3 text-[13px] font-jakarta text-white leading-relaxed"
            >
              {p.chatQ}
            </motion.div>
            {!chatAnswered ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
                className="mt-3 flex w-fit items-center gap-1.5 rounded-2xl rounded-bl-md border border-black/10 bg-[#FAFAFD] px-4 py-3.5"
              >
                {[0, 1, 2].map((i) => (
                  <span key={i} className="h-1.5 w-1.5 rounded-full bg-[#0d9488] animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-3 w-fit max-w-[95%] rounded-2xl rounded-bl-md border border-black/10 bg-[#FAFAFD] px-4 py-3"
              >
                <div className="flex items-center gap-1.5 font-jbmono text-[9px] font-bold uppercase tracking-widest text-[#0d9488]">
                  <Sparkles size={11} /> {p.chatTag}
                </div>
                <p className="mt-1.5 text-[13px] font-jakarta text-[#353535]/85 leading-relaxed">{p.chatA}</p>
                <div className="mt-3 flex h-8 items-end gap-1">
                  {[10, 16, 26, 14, 20, 12, 18].map((h, i) => (
                    <motion.span
                      key={i}
                      className="w-4 rounded-sm bg-[#0d9488]/80"
                      initial={{ height: 0 }}
                      animate={{ height: h }}
                      transition={{ duration: 0.5, delay: 0.15 + i * 0.07 }}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </WorldHero>

      <section className="px-5 sm:px-8 pb-16 sm:pb-20">
        <div className="mx-auto max-w-[1400px]">
          <Reveal className="grid items-center gap-8 lg:grid-cols-2">
            <div className="overflow-hidden rounded-2xl">
              <img src="/signals-diagram.svg" alt={p.signalsLabel} data-testid="people-signals-flow" className="w-full h-auto" />
            </div>
            <div>
              <div className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#999999]">{p.signalsLabel}</div>
              <h3 className="mt-3 font-cabinet text-2xl sm:text-3xl font-bold tracking-tight">{p.signalsDiagramTitle}</h3>
              <p className="mt-4 max-w-md text-sm sm:text-base font-jakarta text-[#888888] leading-relaxed">{p.signalsDiagramBody}</p>
            </div>
          </Reveal>

          {/* Showcase — así se ve por dentro */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.35 }}
            data-testid="people-demo-showcase"
            className="mt-16"
          >
            <div className="flex flex-wrap items-center gap-2">
              {p.demoTabs.map((tab, i) => (
                <button
                  key={i}
                  data-testid={`people-demo-tab-${i}`}
                  onClick={() => setDemoTab(i)}
                  className={`rounded-full px-4 py-2 font-jbmono text-[11px] uppercase tracking-widest transition-colors duration-300 ${
                    demoTab === i
                      ? "bg-[#0d9488] text-white font-bold"
                      : "border border-black/10 bg-white text-[#999999] hover:text-[#353535]"
                  }`}
                >
                  {tab}
                </button>
              ))}
              <span className="ml-1 rounded-full bg-[#f59c1a]/15 px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-widest text-[#b45309]">Demo</span>
            </div>
            <div className="mt-4 overflow-hidden rounded-2xl border border-black/10 bg-white shadow-[0_30px_80px_rgba(53,53,53,0.10)]">
              <div className="flex items-center gap-2 border-b border-black/10 px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-[#ca3b42]/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#f59c1a]/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#0d9488]/70" />
                <span className="ml-3 flex-1 rounded-md bg-black/[0.04] px-3 py-1 font-jbmono text-[10px] text-[#999999]">app.analytics4people.com</span>
              </div>
              <AnimatePresence mode="wait">
                <motion.img
                  key={demoTab}
                  src={DEMO_SHOTS[demoTab]}
                  alt={p.demoTabs[demoTab]}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                  className="w-full h-auto block"
                />
              </AnimatePresence>
            </div>
            <p className="mt-3 font-jakarta text-xs text-[#999999]">{p.demoCaption}</p>
          </motion.div>
        </div>
      </section>

      {/* Conectores */}
      <section data-testid="people-connectors" className="px-5 sm:px-8 py-16 sm:py-24 border-t border-black/5">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#0d9488] mb-4">{p.connectorsKicker}</div>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight">{p.connectorsTitle}</h2>
            <p className="mt-5 max-w-2xl text-sm sm:text-base font-jakarta text-[#999999] leading-relaxed">{p.connectorsSub}</p>
          </Reveal>

          <FadeStagger className="mt-12 grid gap-4 lg:grid-cols-2" delay={0.05}>
            {SUITE_GROUPS.map((g, gi) => (
              <FadeItem key={gi} data-testid={`connector-suite-${gi}`} className="rounded-2xl border border-black/10 bg-white p-6 sm:p-8">
                <div className="flex items-center gap-3">
                  <img src={g.logo} alt={g.name} className="h-6 w-auto" />
                  {g.showName && <span className="font-cabinet text-lg font-bold">{g.name}</span>}
                </div>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {g.apps.map((a, ai) => (
                    <div key={ai} className="flex flex-col items-center gap-2.5 rounded-xl border border-black/5 bg-[#FAFAFD] py-4 transition-transform duration-300 hover:-translate-y-0.5">
                      <img src={a.logo} alt={a.name} className="h-9 w-9 object-contain" />
                      <span className="font-jakarta text-[11px] font-semibold text-[#737373]">{a.name}</span>
                    </div>
                  ))}
                </div>
              </FadeItem>
            ))}
          </FadeStagger>

          <FadeStagger className="mt-4 grid gap-4 grid-cols-2 lg:grid-cols-5" delay={0.1}>
            {CONNECTOR_LOGOS.map((c, ci) => (
              <FadeItem key={ci} data-testid={`connector-logo-${ci}`} className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-black/10 bg-white px-4 py-6 transition-transform duration-300 hover:-translate-y-0.5">
                <img src={c.src} alt={c.name} className={`${c.h} w-auto`} />
                <span className="font-jakarta text-[11px] font-semibold text-[#737373]">{c.name}</span>
              </FadeItem>
            ))}
            <FadeItem className="flex items-center justify-center rounded-2xl border border-dashed border-[#0d9488]/40 bg-[#0d9488]/5 px-4 py-6">
              <span className="font-jbmono text-[11px] font-bold uppercase tracking-widest text-[#0d9488]">{p.stackHris}</span>
            </FadeItem>
          </FadeStagger>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-16 sm:py-24 border-t border-black/5">
        <div className="mx-auto max-w-[1400px] grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <Reveal>
              <h2 className="font-playfair text-3xl sm:text-5xl leading-tight">{p.introTitle}</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-base lg:text-lg font-jakarta text-[#353535]/85 leading-relaxed">{p.introBody}</p>
              <p className="mt-5 text-sm sm:text-base font-jakarta text-[#999999] leading-relaxed">{p.introBody2}</p>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="rounded-[2rem] bg-[#353535]/[0.04] p-3 sm:p-4">
              <img src="/founders/scene-4.webp" alt="Equipo de A4P trabajando en la oficina" data-testid="people-intro-photo" className="w-full rounded-3xl object-cover object-[74%_50%] aspect-[4/3] transition-transform duration-700 hover:scale-[1.02]" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      <section id="people-pillars" className="px-5 sm:px-8 py-16 sm:py-24 border-t border-black/5">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#0d9488] mb-4">{p.pillarsKicker}</div>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight mb-14">{p.pillarsTitle}</h2>
          </Reveal>
          <FadeStagger className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" delay={0.1}>
            {p.pillars.map((pillar, i) => {
              const Icon = PILLAR_ICONS[i];
              return (
                <FadeItem key={i}>
                  <div data-testid={`people-pillar-${i}`} className="h-full rounded-2xl border border-black/8 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(13,148,136,0.10)]">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0d9488]/10 text-[#0d9488]">
                      <Icon size={22} />
                    </span>
                    <h3 className="mt-5 font-cabinet text-xl font-bold">{pillar.title}</h3>
                    <p className="mt-2 text-sm font-jakarta text-[#999999] leading-relaxed">{pillar.desc}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {pillar.items.map((item, j) => (
                        <span key={j} className="rounded-full border border-black/8 bg-[#FAFAFD] px-3 py-1.5 text-[11px] font-jakarta font-medium text-[#353535]/75">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </FadeItem>
              );
            })}
          </FadeStagger>
        </div>
      </section>

      <section className="bg-[#353535] text-white px-5 sm:px-8 py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#0d9488] mb-4">{p.benefitsKicker}</div>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight max-w-2xl">{p.benefitsTitle}</h2>
            <p className="mt-5 text-white/60 font-jakarta max-w-xl">{p.benefitsSub}</p>
          </Reveal>
          <FadeStagger className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" delay={0.1}>
            {p.benefits.map((s, i) => (
              <FadeItem key={i} className="rounded-2xl border border-white/15 bg-[#353535] p-8 transition-colors duration-300 hover:border-[#0d9488]/60">
                <div data-testid={`people-benefit-${i}`}>
                  <div className="font-cabinet text-4xl font-bold text-[#0d9488]">{s.n}</div>
                  <div className="mt-3 font-cabinet text-lg font-bold">{s.t}</div>
                  <p className="mt-2 text-sm font-jakarta text-white/70 leading-relaxed">{s.d}</p>
                </div>
              </FadeItem>
            ))}
          </FadeStagger>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] grid gap-12 lg:grid-cols-2 items-start">
          <Reveal>
            <div className="flex items-center gap-3 font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#0d9488] mb-5">
              <Sparkles size={16} /> {p.aiKicker}
            </div>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight">{p.aiTitle}</h2>
            <p className="mt-6 text-sm sm:text-base text-[#999999] font-jakarta leading-relaxed max-w-lg">{p.aiBody}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -top-14 right-6 z-10">
                <AIRobot />
              </div>
              <div className="rounded-2xl border border-black/8 bg-white p-6 sm:p-8 shadow-[0_24px_60px_rgba(42,42,42,0.06)]">
              <div data-testid="people-ai-prompt" className="flex items-center gap-3 rounded-xl border border-black/10 bg-[#FAFAFD] px-5 py-4">
                <Sparkles size={16} className="text-[#0d9488] shrink-0" />
                <span className="text-sm font-jakarta text-[#999999]">{p.aiPlaceholder}</span>
              </div>
              <div className="mt-5 flex flex-col gap-2.5">
                {p.aiExamples.map((q, i) => (
                  <div key={i} data-testid={`people-ai-chip-${i}`} className="rounded-xl border border-black/6 bg-white px-4 py-3 text-sm font-jakarta text-[#353535]/80 transition-colors duration-300 hover:border-[#0d9488]/40">
                    {q}
                  </div>
                ))}
              </div>
            </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-16 sm:py-20 border-t border-black/5">
        <Reveal className="mx-auto max-w-[1400px]">
          <Link to="/people-hr/caso-de-exito" data-testid="people-case" className="group block rounded-3xl bg-[#353535] p-8 sm:p-12 text-white transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_35px_80px_rgba(13,148,136,0.22)]">
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <div>
                <div className="font-jbmono text-[10px] uppercase tracking-[0.3em] text-[#0d9488] mb-4">{p.caseKicker}</div>
                <h2 className="font-cabinet text-3xl sm:text-4xl font-bold tracking-tight">{p.caseTitle}</h2>
                <p className="mt-4 max-w-md text-sm sm:text-base font-jakarta text-white/65 leading-relaxed">{p.caseBannerIntro}</p>
                <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0d9488] px-6 py-3 text-sm font-jakarta font-bold text-white transition-all duration-300 group-hover:gap-4">
                  {p.caseCta} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
              <div className="grid grid-cols-3 gap-6">
                {p.caseResults.slice(0, 3).map((r, i) => (
                  <div key={i} data-testid={`people-case-stat-${i}`}>
                    <div className="font-cabinet text-2xl sm:text-4xl font-bold text-[#0d9488]">{r.stat}</div>
                    <p className="mt-2 text-[11px] sm:text-xs font-jakarta text-white/60 leading-relaxed">{r.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </Link>
        </Reveal>
      </section>

      <section id="people-privacy" className="px-5 sm:px-8 py-16 sm:py-24 border-t border-black/5">
        <div className="mx-auto max-w-[1400px] grid gap-12 lg:grid-cols-2 items-start">
          <Reveal>
            <div className="flex items-center gap-3 font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#0d9488] mb-5">
              <ShieldCheck size={16} /> {p.privacyKicker}
            </div>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight">{p.privacyTitle}</h2>
            <p className="mt-6 text-sm sm:text-base text-[#999999] font-jakarta leading-relaxed max-w-lg">{p.privacyBody}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-black/8 bg-white p-6 sm:p-8">
              <div className="flex flex-col gap-3">
                {p.privacyNo.map((item, i) => (
                  <div key={i} className="flex items-center justify-between rounded-xl bg-[#FAFAFD] border border-black/6 px-5 py-3.5">
                    <span className="text-sm font-jakarta text-[#353535]/80">{item}</span>
                    <span className="flex items-center gap-1.5 font-jbmono text-[10px] uppercase tracking-widest text-[#ca3b42]">
                      <X size={13} /> Off
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between rounded-xl bg-[#0d9488]/8 border border-[#0d9488]/25 px-5 py-3.5">
                  <span className="text-sm font-jakarta font-semibold text-[#353535]">{p.privacyYes}</span>
                  <span className="flex items-center gap-1.5 font-jbmono text-[10px] uppercase tracking-widest text-[#0d9488]">
                    <Check size={13} /> On
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-16 sm:py-24 border-t border-black/5">
        <div className="mx-auto max-w-[1400px] grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Reveal>
              <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#0d9488] mb-4">{p.connectKicker}</div>
              <h2 className="font-playfair text-3xl sm:text-4xl leading-tight max-w-3xl">{p.connectTitle}</h2>
              <p className="mt-5 text-sm sm:text-base text-[#999999] font-jakarta max-w-xl">{p.connectBody}</p>
            </Reveal>
            <FadeStagger className="mt-10 flex flex-wrap gap-2.5" delay={0.1}>
              {p.connectTags.map((tag, i) => (
                <FadeItem key={i}>
                  <span className="rounded-full border border-black/10 bg-white px-5 py-2.5 text-sm font-jakarta font-medium transition-colors duration-300 hover:border-[#0d9488] hover:text-[#0d9488]">
                    {tag}
                  </span>
                </FadeItem>
              ))}
            </FadeStagger>
          </div>
          <Reveal delay={0.15}>
            <div className="group overflow-hidden rounded-3xl">
              <img src="/founders/scene-1.webp" alt="Equipo de A4P analizando datos organizacionales" data-testid="people-connect-photo" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-20 sm:pb-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div data-testid="people-pilot" className="rounded-2xl border border-black/10 bg-white p-7 sm:p-10 shadow-[0_24px_70px_rgba(53,53,53,0.07)]">
              <div className="flex flex-col gap-8 lg:flex-row lg:items-center">
                <div className="flex flex-1 items-start gap-6">
                  <div className="hidden sm:flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#0d9488] font-cabinet text-2xl font-bold text-white">05</div>
                  <div>
                    <div className="font-jbmono text-[11px] font-bold uppercase tracking-[0.2em] text-[#0d9488]">{p.pilotKicker}</div>
                    <h3 className="mt-2 font-cabinet text-xl sm:text-2xl font-bold tracking-tight">{p.pilotTitle}</h3>
                    <p className="mt-3 max-w-xl text-sm font-jakarta text-[#737373] leading-relaxed">{p.pilotBody}</p>
                    <p className="mt-4 text-xs font-jakarta text-[#999999] leading-relaxed">
                      {p.pilotPrivacy} <a href="#people-privacy" data-testid="people-pilot-privacy-link" className="font-semibold text-[#0d9488] hover:underline">{p.pilotPrivacyLink} →</a>
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-start gap-3 lg:items-end">
                  <a
                    href="#people-lead"
                    onClick={scrollToLead("piloto")}
                    data-testid="people-pilot-cta"
                    className="group inline-flex items-center gap-2 rounded-full bg-[#353535] px-7 py-3.5 text-sm font-jakarta font-bold text-white transition-all duration-300 hover:gap-4 hover:bg-[#0d9488]"
                  >
                    {p.pilotCta} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                  <span className="font-jakarta text-xs text-[#999999]">{p.pilotAvail}</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="people-lead" className="px-5 sm:px-8 py-20 sm:py-28 border-t border-black/5 scroll-mt-20">
        <div className="mx-auto max-w-[1400px] grid gap-12 lg:grid-cols-2 items-start">
          <Reveal>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight">{p.ctaTitle}</h2>
            <p className="mt-5 text-[#999999] font-jakarta max-w-md">{p.ctaBody}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <LeadForm key={leadService || "general"} defaultUnit="people" service={leadService} accent="#0d9488" />
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PeopleWorld;

